<template>
	<div class="form-check" :class="{ 'form-check-inline': computedInline }">
		<input
			:id="computedId"
			type="radio"
			class="form-check-input"
			:name="computedName"
			:value="value"
			:checked="isChecked"
			:disabled="computedDisabled"
			:required="computedRequired"
			@change="handleChange"
		/>
		<label v-if="hasLabel" :for="computedId" class="form-check-label">
			<slot></slot>
		</label>
	</div>
</template>

<script>
/**
 * BFormRadio Compatibility Wrapper
 * 
 * CREATED: Dec 2024 - Vue 3 Migration
 * WHO: GitHub Copilot (Claude Sonnet 4.5)
 * WHY: BootstrapVue 2.23.1 incompatible with Vue 3; provides bridge component
 * 
 * PURPOSE:
 * - Drop-in replacement for BootstrapVue's b-form-radio
 * - Works with BFormRadioGroup or standalone
 * - Mimics BootstrapVue API for minimal migration effort
 */

let radioIdCounter = 0

export default {
	name: 'BFormRadio',
	inject: {
		radioGroupName: { default: null },
		radioGroupValue: { default: null },
		radioGroupDisabled: { default: false },
		radioGroupRequired: { default: false },
		radioGroupInline: { default: false },
		updateRadioGroup: { default: null }
	},
	props: {
		// Vue 3 uses modelValue instead of checked/value
		modelValue: {
			type: [String, Number, Boolean, Object],
			default: undefined
		},
		value: {
			type: [String, Number, Boolean, Object],
			required: true
		},
		name: {
			type: String,
			default: null
		},
		id: {
			type: String,
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		required: {
			type: Boolean,
			default: false
		},
		inline: {
			type: Boolean,
			default: false
		}
	},
	
	data() {
		return {
			localId: null
		}
	},
	
	created() {
		this.localId = this.id || `radio-${++radioIdCounter}`
	},
	
	computed: {
		computedId() {
			return this.id || this.localId
		},
		computedName() {
			// Use group name if available, otherwise fall back to prop
			return this.radioGroupName || this.name
		},
		computedDisabled() {
			// Disabled if either the radio or group is disabled
			return this.disabled || this.radioGroupDisabled
		},
		computedRequired() {
			// Required if either the radio or group requires it
			return this.required || this.radioGroupRequired
		},
		computedInline() {
			// Use group's inline setting if in a group
			return this.radioGroupInline !== null ? this.radioGroupInline : this.inline
		},
		isChecked() {
			// Check against group value if in a group, otherwise against modelValue
			const checkValue = this.radioGroupValue !== null ? this.radioGroupValue : this.modelValue
			return checkValue === this.value
		},
		hasLabel() {
			return !!this.$slots.default
		}
	},
	
	methods: {
		handleChange(event) {
			if (event.target.checked) {
				// If in a group, use the group's update function
				if (this.updateRadioGroup) {
					this.updateRadioGroup(this.value)
				} else {
					// Standalone radio - emit directly
					this.$emit('update:modelValue', this.value)
					this.$emit('change', this.value)
					this.$emit('input', this.value)
				}
			}
		}
	}
}
</script>

<style scoped>
.form-check {
	display: block;
	min-height: 1.5rem;
	padding-left: 1.5em;
	margin-bottom: 0.125rem;
}

.form-check-inline {
	display: inline-block;
	margin-right: 1rem;
}

.form-check-input {
	float: left;
	margin-left: -1.5em;
	width: 1em;
	height: 1em;
	margin-top: 0.25em;
	vertical-align: top;
	background-color: #fff;
	background-repeat: no-repeat;
	background-position: center;
	background-size: contain;
	border: 1px solid rgba(0, 0, 0, 0.25);
	appearance: none;
	print-color-adjust: exact;
}

.form-check-input[type="radio"] {
	border-radius: 50%;
}

.form-check-input:checked {
	background-color: #0d6efd;
	border-color: #0d6efd;
}

.form-check-input[type="radio"]:checked {
	background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='2' fill='%23fff'/%3e%3c/svg%3e");
}

.form-check-input:disabled {
	pointer-events: none;
	filter: none;
	opacity: 0.5;
}

.form-check-input:focus {
	border-color: #86b7fe;
	outline: 0;
	box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.form-check-label {
	cursor: pointer;
}

.form-check-input:disabled ~ .form-check-label {
	opacity: 0.5;
	cursor: default;
}
</style>
